import axios from "axios";
import BaseApi from "./base-api";
import { LocalizedText } from "../types/LocalizedText";

// export type LinkType = "product" | "category" | "external";
export type Position = "home_main" | "home_secondary";

export type BannerMedia = string | string[];

export interface BannerPayload {
  id?: string;
  title: LocalizedText;
  image?: BannerMedia;
  mobileImage?: BannerMedia;
  video?: BannerMedia;
  mobileVideo?: BannerMedia;
  videoUrl?: string;
  mediaType?: "image" | "video" | string;
  position: Position;
  isActive: boolean;
  sortOrder: number;
  startDate?: string;
  endDate?: string;
}

export interface BannerAnalytics {
  views: number;
  clicks: number;
  ctr: number; // Click-through rate
}

export interface TrackingPayload {
  userId?: string;
  sessionId?: string;
  timestamp?: string;
  userAgent?: string;
}

export interface ReorderItem {
  id: string;
  sortOrder: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  [key: string]: any; // Allow additional properties for flexibility
}

export interface BannerUploadResponse {
  url: string;
  key?: string;
  mediaType?: string;
  filename?: string;
  size?: number;
}

export const BannerApi = {
  // PUBLIC APIs
  /**
   * Get all active public banners for display
   */
  getPublic: (): Promise<ApiResponse<BannerPayload[]>> =>
    BaseApi.get("/banners/public"),

  // ADMIN APIs
  /**
   * Get all banners (admin only)
   */
  getAll: (): Promise<ApiResponse<BannerPayload[]>> => BaseApi.get("/banners"),

  /**
   * Get a specific banner by ID
   */
  getById: (id: string): Promise<ApiResponse<BannerPayload>> =>
    BaseApi.get(`/banners/${id}`),

  /**
   * Create a new banner
   */
  create: (payload: BannerPayload): Promise<ApiResponse<BannerPayload>> =>
    BaseApi.post("/banners", payload),

  /**
   * Update an existing banner
   */
  update: (
    id: string,
    payload: Partial<BannerPayload>,
  ): Promise<ApiResponse<BannerPayload>> =>
    BaseApi.put(`/banners/${id}`, payload),

  /**
   * Delete a banner
   */
  delete: (id: string): Promise<ApiResponse<void>> =>
    BaseApi.delete(`/banners/${id}`),

  /**
   * Toggle banner active status
   */
  toggle: (id: string): Promise<ApiResponse<BannerPayload>> =>
    BaseApi.patch(`/banners/${id}/toggle`),

  /**
   * Reorder banners
   */
  reorder: (items: ReorderItem[]): Promise<ApiResponse<void>> =>
    BaseApi.post("/banners/reorder", { items }),

  // ANALYTICS APIs
  /**
   * Get banner analytics
   */
  analytics: (id: string): Promise<ApiResponse<BannerAnalytics>> =>
    BaseApi.get(`/banners/${id}/analytics`),

  /**
   * Track banner view
   */
  trackView: (
    id: string,
    payload?: TrackingPayload,
  ): Promise<ApiResponse<void>> =>
    BaseApi.post(`/banners/${id}/track/view`, payload || {}),

  /**
   * Track banner click
   */
  trackClick: (
    id: string,
    payload?: TrackingPayload,
  ): Promise<ApiResponse<void>> =>
    BaseApi.post(`/banners/${id}/track/click`, payload || {}),

  /**
   * Upload multiple banner images
   */
  uploadImages: (files: File[]): Promise<ApiResponse<{ urls: string[] }>> => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`images`, file);
    });

    return BaseApi.post("/banners/upload/multiple", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /**
   * Upload a banner image
   * @returns The URL of the uploaded image
   */
  uploadImage: async (file: File): Promise<ApiResponse<{ url: string }>> => {
    // if (!file) return err("Select an image first");

    const formData = new FormData();
    formData.append("image", file); // MUST match backend field name

    try {
      // setLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/banners/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log("Upload success:", res.data);
      alert("Image uploaded successfully");
      return res.data;
    } catch (err) {
      console.error(err);
      alert("Upload failed");
      throw err;
    } finally {
      // setLoading(false);
    }
  },

  uploadMedia: async (
    file: File,
  ): Promise<ApiResponse<BannerUploadResponse>> => {
    const formData = new FormData();
    formData.append("media", file);

    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/banners/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );

    return res.data;
  },
};
